/**
 * CadenceSectionPure
 *
 * Pure presentational component for rendering cadence steps.
 * Works in both Next.js (with tRPC) and Electron (with IPC) by receiving
 * all data and callbacks via props.
 */
import type { ReactNode } from 'react';
import { useState, useMemo } from 'react';

import {
  CGStepPure,
  type EditStepPure,
  type CreateStepData,
  type CadenceGraphNodesPure,
  type StepUpdateInputPure,
  type SiftConfig,
} from '../CGStep';
import { FirstAddStep } from './FirstAddStep';
import { UserAtVariables, ContactAtVariables } from '@poseidon/types/types/atVariables.type';
import type { StepHistoryOption, StepResolution } from '../CampaignDayCard/stepResolution';
import type { PreviousEmailThread } from './previousEmailThread';
import type { TipTapVariable, TipTapDocument } from '../Input2/TipTapInput';

const USER_AT_VARS_SET = new Set<string>(UserAtVariables);
const CONTACT_AT_VARS_SET = new Set<string>(ContactAtVariables);

export type AIMessageTeamLogo = {
  url: string;
  webp?: string;
} | null;

// ============================================================================
// Types
// ============================================================================

export type CadenceStepState = 'not-started' | 'interrupted' | 'skipped' | 'finished' | 'canceled' | 'action-required' | 'responded' | 'contact_unavailable';

export type CadenceGraph = {
  startId: string | null;
  nodes: CadenceGraphNodesPure;
};

export type CadenceData = {
  id: string;
  templateId?: string | null;
  createdOn: Date;
};

export type TemplateInfo = {
  goal?: string | null;
  previewText?: string | null;
  recentPosts?: string[];
};

export type ContactInfo = {
  id: string;
  name?: string;
  company?: string;
  title?: string;
  headline?: string;
  about?: string;
  city?: string;
  /**
   * True when the auth user is already a LinkedIn connection of this contact.
   * Drives the "Connect" picker option's disabled state in the cadence card.
   */
  alreadyConnectedOnLinkedIn?: boolean;
};

export type DocumentData = {
  id: string;
  title?: string;
  url?: string;
  imageUrl?: string;
};

export type StepCreateInput = {
  type: string;
  body2?: string | null;
  subject?: string | null;
  delay?: number;
  afterStepId?: string | null;
  option?: 'one' | 'two';
  cc?: string;
  bcc?: string;
  onlyOpenLink?: boolean;
  postsToLike?: number;
  siftConditions?: SiftConfig;
  /** Email only: continue the previous email's thread. Absent ⇒ DB default (true). */
  replyToPrevious?: boolean;
};

export type StepUpdateInput = StepUpdateInputPure;

export type AIMessageSectionData = {
  isSuperAdmin: boolean;
  canEditTeamData: boolean;
  teamName: string;
  teamLogo?: AIMessageTeamLogo;
  userProfilePicture?: string | null;
  userName?: string | null;
  takeActionTemplate: string | null;
  teamAiInfo: string | null;
  workContext: string | null;
  isLoading?: boolean;
};

// ============================================================================
// Component Props
// ============================================================================

export type CadenceSectionPureProps = {
  // Data
  cadence: CadenceData;
  stepsGraph: CadenceGraph | null;
  templateInfo?: TemplateInfo | null;
  documents?: DocumentData[];
  contactInfo: ContactInfo;
  atVariables: Record<string, string>;

  // Callbacks. All optional — when ANY is missing the section renders in
  // read-only mode: the step plan still displays, but every edit
  // affordance (add-step pickers, click-to-edit rows, delay pills,
  // mark-done, delete) is hidden. Hosts that can't wire the step
  // mutations (e.g. the extension sidepanel) simply omit them.
  onCreateStep?: (data: StepCreateInput) => unknown;
  onUpdateStep?: (stepId: string, data: StepUpdateInput) => unknown;
  onDeleteStep?: (stepId: string) => unknown;
  onChangeStepType?: (stepId: string, type: string) => unknown;
  onResolveStep?: (stepId: string, resolution: StepResolution) => unknown;
  /**
   * The contact's history rows the step editor's finish / skip popup offers
   * to link to the step (see `toStepHistoryOptions`).
   */
  stepHistoryOptions?: StepHistoryOption[];
  /**
   * The cadence owner's latest email thread with this contact (`getPreviousEmailThread` scoped to
   * the cadence's user), or null. Email step editors thread onto it — see `findCadenceThreadSubject`.
   */
  cadenceEmailThread?: PreviousEmailThread | null;
  onToggleInmailOpen?: (nodeId: string, onlyOpenLink: boolean) => unknown;

  // Render props for platform-specific components
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => ReactNode;

  // Reminder editor — list of available reminder actions for the team and a
  // create-on-the-fly callback. When provided, the inline reminder editor
  // shows them in the dropdown and lets the user type a new name to add.
  reminderActions?: { id: string; name: string }[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;

  // Optional
  className?: string;
  voiceCallingEnabled?: boolean;
  /**
   * Injected dialer for an action-required `call` step. Forwarded down the step tree to
   * the timeline row (`CampaignStepRowPure`) so the rep can place the call in place.
   * Omitted by read-only hosts (extension sidepanel), so the button simply won't render.
   */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => ReactNode;
  /** Completes a `call` step handled outside the dialer — see `CallStepActionsPure`. */
  onMarkCallStepDone?: (args: { cadenceStepId: string; outcome: string; notes?: string; callbackAt?: number }) => Promise<void> | void;
  /**
   * Origin prefixed to the "See in template" link (e.g.
   * `https://app.useposeidon.com`). The dashboard omits it (relative
   * link); the extension/electron must pass it — a relative href inside
   * a non-dashboard origin resolves to a broken URL.
   */
  templateLinkBaseUrl?: string;
};

// ============================================================================
// Component
// ============================================================================

export function CadenceSectionPure({
  cadence,
  stepsGraph,
  templateInfo: _templateInfo,
  documents,
  contactInfo,
  atVariables,
  onCreateStep,
  onUpdateStep,
  onDeleteStep,
  onChangeStepType,
  onResolveStep,
  stepHistoryOptions,
  cadenceEmailThread,
  onToggleInmailOpen,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  className,
  voiceCallingEnabled,
  renderCadenceCallButton,
  onMarkCallStepDone,
  templateLinkBaseUrl,
}: CadenceSectionPureProps) {
  const [editingStep, setEditingStep] = useState<EditStepPure | undefined>();

  const hasSteps = stepsGraph?.nodes != null && stepsGraph?.startId != null;

  // Read-only when any step mutation is missing — display the plan,
  // hide all edit affordances. All-or-nothing mirrors the previous
  // host-side gate that required every callback to render at all.
  const readOnly =
    onCreateStep == null ||
    onUpdateStep == null ||
    onDeleteStep == null ||
    onChangeStepType == null ||
    onResolveStep == null ||
    onToggleInmailOpen == null;

  // Convert atVariables to TipTapVariable[]. Group by whether the key is a
  // known user-scoped variable (My*, Today) vs a contact-scoped variable so
  // the TipTap mention dropdown can split them under "My Variables" /
  // "Contact Variables" headers.
  const tipTapVariables = useMemo<TipTapVariable[]>(() => {
    return Object.entries(atVariables).map(([key, value]) => {
      let group: 'user' | 'contact' | undefined;
      if (USER_AT_VARS_SET.has(key) || key === 'Today') group = 'user';
      else if (CONTACT_AT_VARS_SET.has(key)) group = 'contact';
      else group = 'contact';
      // Chip display text: the resolved VALUE for the current contact
      // (e.g. "Michael") when one exists, falling back to "@FirstName"
      // when the variable has no resolution yet (template editor / new
      // contact). The mention chip is already styled blue + underlined
      // via the `.mention` class (see TipTapInput.tsx editor styles),
      // so the user sees "Michael" in blue and knows it'll be
      // re-resolved per contact at send time. Variable identity is
      // preserved via `data-id` regardless of what's in `data-label`.
      return {
        id: key,
        label: value || `@${key}`,
        group,
      };
    });
  }, [atVariables]);

  // Convert documents to TipTapDocument[]
  const tipTapDocuments = useMemo<TipTapDocument[]>(() => {
    return (documents ?? []).map((doc) => ({
      id: doc.id,
      label: doc.title ?? 'Untitled',
      imageUrl: doc.imageUrl,
    }));
  }, [documents]);

  // Handle create step - convert CreateStepData to StepCreateInput
  const handleCreateStep = async (data: CreateStepData) => {
    await onCreateStep?.({
      type: data.type,
      body2: data.body2,
      subject: data.subject,
      delay: data.delay,
      afterStepId: data.afterStepId,
      option: data.option,
      cc: data.cc,
      bcc: data.bcc,
      onlyOpenLink: data.onlyOpenLink,
      postsToLike: data.postsToLike,
      siftConditions: data.siftConditions,
      replyToPrevious: data.replyToPrevious,
    });
  };

  // Read-only with nothing to show — skip the section entirely rather
  // than rendering an empty timeline shell.
  if (readOnly && !hasSteps) return null;

  return (
    <div className={className}>
      <div className="flex flex-col" id="scheduled_divider">
        <div className="flex flex-col">

          {/* "Add a step at the start of the cadence" affordance.
              Renders for both empty and populated cadences — afterStepId
              is undefined so the new step lands BEFORE any existing
              first step. Same component / chrome as the trailing
              new-day trigger so the start and end of the timeline read
              consistently. Hidden in read-only mode. */}
          {!readOnly && (
            <FirstAddStep
              editingStep={editingStep}
              setEditingStep={setEditingStep}
              variables={tipTapVariables}
              documents={tipTapDocuments}
              // An empty cadence has no earlier step — the owner's real thread is all there is to join.
              previousEmailSubject={cadenceEmailThread?.subject ?? null}
              renderSiftConditionBuilder={renderSiftConditionBuilder}
              reminderActions={reminderActions}
              reminderActionsLoading={reminderActionsLoading}
              onCreateReminder={onCreateReminder}
              reminderCreating={reminderCreating}
              onReminderError={onReminderError}
              alreadyConnectedToContact={contactInfo.alreadyConnectedOnLinkedIn}
              voiceCallingEnabled={voiceCallingEnabled}
              onCreateStep={handleCreateStep}
            />
          )}

          {/* Steps list */}
          {hasSteps && (
            <>
              {/* Render the step tree */}
              <CGStepPure
                nodeId={stepsGraph.startId!}
                nodes={stepsGraph.nodes}
                first
                msUpToNow={cadence.createdOn?.valueOf() ?? Date.now()}
                alreadyConnectedToContact={contactInfo.alreadyConnectedOnLinkedIn}
                templateHref={
                  cadence.templateId != null
                    ? `${templateLinkBaseUrl ?? ''}/templates/${cadence.templateId}`
                    : undefined
                }
                reminderActions={reminderActions}
                reminderActionsLoading={reminderActionsLoading}
                onCreateReminder={onCreateReminder}
                reminderCreating={reminderCreating}
                onReminderError={onReminderError}
                editingStep={editingStep}
                setEditingStep={setEditingStep}
                variables={tipTapVariables}
                documents={tipTapDocuments}
                contactId={contactInfo.id}
                // CGStepPure's callbacks are required; in read-only mode
                // they're unreachable (every affordance is hidden), so
                // no-ops keep the contract without changing behavior.
                onChangeType={onChangeStepType ?? (() => {})}
                onDeleteNode={onDeleteStep ?? (() => {})}
                onCreateStep={handleCreateStep}
                onUpdateStep={onUpdateStep ?? (() => {})}
                onToggleInmailOpen={onToggleInmailOpen ?? (() => {})}
                resolveStep={onResolveStep ?? (() => {})}
                stepHistoryOptions={stepHistoryOptions}
                cadenceEmailThread={cadenceEmailThread}
                renderSiftConditionBuilder={renderSiftConditionBuilder}
                voiceCallingEnabled={voiceCallingEnabled}
                renderCadenceCallButton={renderCadenceCallButton}
                onMarkCallStepDone={onMarkCallStepDone}
                readOnly={readOnly}
              />
            </>
          )}
        </div>
        <div className="h-20" />
      </div>
    </div>
  );
}
